"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAuth } from "../../lib/hooks/useAuth";
import { useSession } from "next-auth/react";
import { Button } from "../ui/Button";
import { useRouter } from "next/navigation";
import {
  User,
  LogOut,
  ChevronDown,
  FileText,
  BadgeCheck,
  TrendingUp,
  CalendarDays,
  ClipboardList,
  LayoutDashboard,
} from "lucide-react";
import toast from "react-hot-toast";

const ACTIVITY_LINKS = [
  {
    href: "/profile/activity",
    label: "내 활동",
    icon: TrendingUp,
  },
  {
    href: "/profile/growth",
    label: "성장 리포트",
    icon: TrendingUp,
  },
  {
    href: "/profile/reports",
    label: "음악 리포트",
    icon: FileText,
  },
  {
    href: "/profile/expert-analysis",
    label: "피드백 신청내역",
    icon: ClipboardList,
  },
  {
    href: "/profile/lessons",
    label: "레슨 예약",
    icon: CalendarDays,
  },
] as const;

export function ProfileDropdown() {
  const { user, loading, logout } = useAuth();
  const { data: session } = useSession();
  const router = useRouter();

  // NextAuth 세션이 있으면 Firebase Auth 대신 사용
  const isAuthenticated = useMemo(
    () => user || session?.user,
    [user, session?.user]
  );
  const currentUser = useMemo(
    () => user || session?.user,
    [user, session?.user]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isInstructor, setIsInstructor] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 강사 여부 확인
  useEffect(() => {
    let isMounted = true;

    const checkInstructorStatus = async () => {
      if (!user?.uid) {
        setIsInstructor(false);
        return;
      }

      try {
        const { getDb } = await import("../../lib/api/firebase-lazy");
        const { doc, getDoc } = await import("firebase/firestore");

        const db = getDb();
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const instructorDoc = await getDoc(doc(db, "instructors", user.uid));

        const hasInstructorFlag = userDoc.data()?.isInstructor === true;
        const hasInstructorProfile = instructorDoc.exists();

        if (isMounted) {
          // instructors 컬렉션에 문서가 있으면 무조건 강사로 취급
          setIsInstructor(hasInstructorProfile || hasInstructorFlag);
        }
      } catch (error) {
        if (isMounted) {
          console.error("강사 인증 확인 실패:", error);
          setIsInstructor(false);
        }
      }
    };

    checkInstructorStatus();

    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      setIsOpen(false);
      // 인증 가드가 있는 페이지에 머물면 로그아웃 직후 /auth/login으로 다시 보낼 수 있음
      router.replace("/");

      if (user) {
        await logout();
      }

      if (session) {
        const { signOut } = await import("next-auth/react");
        await signOut({ redirect: false });
      }

      toast.success("로그아웃되었습니다.");
    } catch (error) {
      console.error("로그아웃 실패:", error);
      toast.error("로그아웃 중 오류가 발생했습니다.");
      router.replace("/");
    }
  };

  if (loading) {
    return (
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center space-x-2">
        <Link href="/auth/login">
          <Button
            variant="ghost"
            size="sm"
            className="px-4 py-2 bg-gradient-to-r from-point1 to-point2 hover:from-secondary hover:to-point text-white rounded-full font-medium transition-all duration-300 shadow-sm hover:shadow-md"
          >
            로그인
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 프로필 버튼 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 hover:bg-white/10 rounded-full p-1 transition-colors"
      >
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0 border-2 border-gray-300">
          {(currentUser as any)?.image || user?.profileImage ? (
            <Image
              src={
                ((currentUser as any)?.image ||
                  user?.profileImage ||
                  "") as string
              }
              alt={
                ((currentUser as any)?.name ||
                  user?.nickname ||
                  "프로필 이미지") as string
              }
              width={32}
              height={32}
              className="w-8 h-8 rounded-full object-cover"
              style={{ width: "32px", height: "32px" }}
              unoptimized
            />
          ) : (
            <span className="text-sm font-medium text-gray-600">
              {(
                ((currentUser as any)?.name || user?.nickname) as
                  | string
                  | undefined
              )
                ?.charAt(0)
                ?.toUpperCase()}
            </span>
          )}
        </div>
        <span className="hidden sm:block text-sm font-medium text-white">
          {(((currentUser as any)?.name || user?.nickname) as
            | string
            | undefined) || ""}
        </span>
        <ChevronDown className="w-4 h-4 text-white/80 hidden sm:block" />
      </button>

      {/* 드롭다운 메뉴 */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[100]">
          {/* 사용자 정보 */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                {(currentUser as any)?.image || user?.profileImage ? (
                  <Image
                    src={
                      (((currentUser as any)?.image || user?.profileImage) as
                        | string
                        | undefined) || ""
                    }
                    alt={
                      (((currentUser as any)?.name || user?.nickname) as
                        | string
                        | undefined) || "프로필 이미지"
                    }
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-full object-cover"
                    style={{ width: "40px", height: "40px" }}
                    unoptimized
                  />
                ) : (
                  <span className="text-lg font-medium text-gray-600">
                    {(
                      ((currentUser as any)?.name || user?.nickname) as
                        | string
                        | undefined
                    )
                      ?.charAt(0)
                      ?.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {(((currentUser as any)?.name || user?.nickname) as
                      | string
                      | undefined) || ""}
                  </span>
                  {isInstructor && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-900 text-white text-[10px] font-semibold rounded">
                      <BadgeCheck className="w-3 h-3" />
                      <span>PRO</span>
                    </span>
                  )}
                </div>
                {/* 이메일 제거됨 */}
              </div>
            </div>
          </div>

          {/* 프로: 스튜디오 바로가기 */}
          {isInstructor && (
            <div className="border-b border-gray-200 py-1">
              <Link
                href="/instructor/dashboard"
                className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50"
                onClick={() => setIsOpen(false)}
              >
                <LayoutDashboard className="mr-3 h-4 w-4 text-gray-500" />
                프로 스튜디오
              </Link>
            </div>
          )}

          {/* 마이페이지 */}
          <div className="border-b border-gray-100 py-1">
            <Link
              href="/profile"
              className="flex items-center px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <User className="mr-3 h-4 w-4 text-gray-500" />
              마이페이지
            </Link>
          </div>

          {/* 내 활동 — 빠른 진입 */}
          <div className="border-b border-gray-100 py-1">
            <p className="px-4 pb-1 pt-2 text-[11px] font-medium text-stone-400">
              내 활동
            </p>
            {ACTIVITY_LINKS.map(item => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className="flex items-center px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => setIsOpen(false)}
                >
                  <Icon className="mr-3 h-4 w-4 text-gray-500" />
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* 로그아웃 */}
          <div className="py-1">
            <button
              onClick={handleLogout}
              className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
            >
              <LogOut className="mr-3 h-4 w-4" />
              로그아웃
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
